import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import PageLayout from '../../components/PageLayout';
import EntityDrawer from '../../components/ui/EntityDrawer';
import {
  InquirySettings,
  TripInquiry,
  inquiryError,
  inquiryLabel,
  inquiryMoney,
  tripInquiryAPI,
  tripSourcingAPI,
} from '../../services/tripInquiry';
import { OpportunitySourcing } from './AdminTripSourcing';
import { inquiryDateTime, inquiryWallTime } from '../../utils/inquiryDateTime';
const stages = [
  'new',
  'reviewing',
  'awaiting_information',
  'sourcing',
  'lost',
  'spam',
];
function SettingsForm({
  settings,
  close,
}: {
  settings: InquirySettings;
  close: () => void;
}) {
  const [draft, setDraft] = useState(settings);
  const cache = useQueryClient();
  const save = useMutation(
    () =>
      tripInquiryAPI.updateSettings({
        ...draft,
        operatorEmail: draft.operatorEmail?.trim() || null,
      }),
    {
      onSuccess: () => {
        cache.invalidateQueries('trip-inquiry-settings');
        cache.invalidateQueries('public-trip-form');
        close();
      },
    }
  );
  return (
    <form
      className="admin-subpanel rounded-card p-6 space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <div className="flex justify-between">
        <div>
          <h2 className="font-semibold text-xl">Customer inquiry form</h2>
          <p className="text-sm text-on-surface-variant mt-1">
            A tenant-owned form with local inquiry capture and ESP
            notifications.
          </p>
        </div>
        <button type="button" onClick={close} aria-label="Close form settings">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <label className="flex gap-3 items-center">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(event) =>
            setDraft({ ...draft, enabled: event.target.checked })
          }
        />{' '}
        Accept public trip inquiries
      </label>
      <label className="flex gap-3 items-center">
        <input
          type="checkbox"
          checked={!!draft.chatEnabled}
          onChange={(event) =>
            setDraft({ ...draft, chatEnabled: event.target.checked })
          }
        />
        Enable the customer booking assistant
      </label>
      <p className="text-sm text-on-surface-variant">
        The assistant uses the platform’s central AI connection. Customers
        review the trip before sending an inquiry; your team controls quoting
        and confirmation.
      </p>
      <div className="grid md:grid-cols-2 gap-5">
        <label className="block text-sm">
          Form mode
          <select
            className="input-field mt-2 w-full"
            value={draft.mode}
            onChange={(event) =>
              setDraft({
                ...draft,
                mode: event.target.value as InquirySettings['mode'],
              })
            }
          >
            <option value="collect_only">
              Collect requests for team quoting
            </option>
            <option value="estimate">
              Show indicative inventory estimates
            </option>
          </select>
        </label>
        <label className="block text-sm">
          Operator notification email
          <input
            className="input-field mt-2 w-full"
            type="email"
            maxLength={254}
            value={draft.operatorEmail || ''}
            onChange={(event) =>
              setDraft({ ...draft, operatorEmail: event.target.value })
            }
            placeholder="Leave blank to use company support email"
          />
        </label>
      </div>
      <label className="block text-sm">
        Public guidance
        <textarea
          className="input-field w-full mt-2"
          rows={3}
          maxLength={2000}
          value={draft.notice}
          onChange={(event) =>
            setDraft({ ...draft, notice: event.target.value })
          }
        />
      </label>
      <p className="text-sm text-on-surface-variant">
        Notifications use your existing ESP connection and tenant sending
        policy. Requests remain saved if email delivery is delayed.
      </p>
      {!!save.error && (
        <p role="alert" className="text-error">
          {inquiryError(save.error)}
        </p>
      )}
      <button className="btn-primary" disabled={save.isLoading}>
        Save form settings
      </button>
    </form>
  );
}
function ItineraryEditor({
  inquiry,
  onChange,
}: {
  inquiry: TripInquiry;
  onChange: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(inquiry.trip_json);
  const [start, setStart] = useState(() =>
    inquiryWallTime(inquiry.trip_json.startDate, inquiry.trip_json.timezone)
  );
  const [end, setEnd] = useState(() =>
    inquiryWallTime(inquiry.trip_json.endDate, inquiry.trip_json.timezone)
  );
  const save = useMutation(
    async () => {
      const trip = {
        ...draft,
        startDate: inquiryDateTime(start, draft.timezone),
        endDate: inquiryDateTime(end, draft.timezone),
      };
      return tripInquiryAPI.editTrip(inquiry.id, inquiry.version, trip);
    },
    {
      onSuccess: () => {
        setOpen(false);
        onChange();
      },
    }
  );
  if (!open)
    return (
      <button className="btn-secondary" onClick={() => setOpen(true)}>
        Edit / complete itinerary
      </button>
    );
  return (
    <form
      className="admin-subpanel rounded-card p-5 space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <h3 className="font-semibold">Complete the service itinerary</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="text-sm">
          Pickup
          <input
            required
            maxLength={500}
            className="input-field w-full mt-2"
            value={draft.pickupLocation}
            onChange={(event) =>
              setDraft({ ...draft, pickupLocation: event.target.value })
            }
          />
        </label>
        <label className="text-sm">
          Destination
          <input
            required
            maxLength={500}
            className="input-field w-full mt-2"
            value={draft.dropoffLocation}
            onChange={(event) =>
              setDraft({ ...draft, dropoffLocation: event.target.value })
            }
          />
        </label>
        <label className="text-sm">
          Departure ({draft.timezone})
          <input
            type="datetime-local"
            className="input-field w-full mt-2"
            value={start}
            onChange={(event) => setStart(event.target.value)}
          />
        </label>
        <label className="text-sm">
          Service end ({draft.timezone})
          <input
            type="datetime-local"
            className="input-field w-full mt-2"
            value={end}
            onChange={(event) => setEnd(event.target.value)}
          />
        </label>
        <label className="text-sm">
          Passengers
          <input
            type="number"
            min="1"
            max="10000"
            className="input-field w-full mt-2"
            value={draft.passengerCount || ''}
            onChange={(event) =>
              setDraft({
                ...draft,
                passengerCount: event.target.value
                  ? Number(event.target.value)
                  : undefined,
              })
            }
          />
        </label>
        <label className="text-sm">
          Vehicle preference
          <input
            maxLength={100}
            className="input-field w-full mt-2"
            value={draft.vehicleType || ''}
            onChange={(event) =>
              setDraft({
                ...draft,
                vehicleType: event.target.value || undefined,
              })
            }
          />
        </label>
      </div>
      <label className="block text-sm">
        Operational notes
        <textarea
          className="input-field w-full mt-2"
          rows={3}
          maxLength={5000}
          value={draft.serviceNotes || ''}
          onChange={(event) =>
            setDraft({
              ...draft,
              serviceNotes: event.target.value || undefined,
            })
          }
        />
      </label>
      <p className="text-xs text-on-surface-variant">
        Updating the itinerary invalidates fulfillment approval. Review the
        linked quote before approving again.
      </p>
      {!!save.error && (
        <p role="alert" className="text-error">
          {inquiryError(save.error)}
        </p>
      )}
      <div className="flex gap-3">
        <button className="btn-primary" disabled={save.isLoading}>
          Save itinerary
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
function BookingPlanReview({ inquiry }: { inquiry: TripInquiry }) {
  const plan = inquiry.trip_json.bookingPlan;
  if (!plan) return null;
  const time = (value?: string) =>
    value
      ? new Date(value).toLocaleString(undefined, {
          timeZone: inquiry.trip_json.timezone,
        })
      : 'To be discussed';
  const requirements = (vehicles?: typeof plan.vehicleRequirements) =>
    vehicles?.length ? (
      <ul className="space-y-2 mt-2">
        {vehicles.map((vehicle, index) => (
          <li key={index} className="text-sm">
            {vehicle.quantity || 'Unspecified number of'} ×{' '}
            {vehicle.vehicleType || 'Vehicle type to be discussed'}
            {vehicle.passengerCapacity &&
              ` · ${vehicle.passengerCapacity} passenger capacity per vehicle`}
            {vehicle.notes && (
              <p className="whitespace-pre-wrap text-on-surface-variant">
                {vehicle.notes}
              </p>
            )}
          </li>
        ))}
      </ul>
    ) : null;
  return (
    <section className="admin-subpanel p-5 rounded-card space-y-4">
      <div>
        <h3 className="font-semibold">Complete booking plan</h3>
        <p className="text-sm text-on-surface-variant mt-1">
          Review every service segment and vehicle requirement before preparing
          the quote.
        </p>
      </div>
      {requirements(plan.vehicleRequirements)}
      <ol className="space-y-4">
        {plan.segments.map((segment, index) => (
          <li
            key={index}
            className="border-t border-outline-variant pt-4 text-sm"
          >
            <h4 className="font-semibold">
              {index + 1}. {segment.label || 'Service segment'}
            </h4>
            <p className="mt-2">
              {segment.pickupLocation || 'Pickup to be discussed'} →{' '}
              {segment.dropoffLocation || 'Destination to be discussed'}
            </p>
            <p>
              {time(segment.startDate)} → {time(segment.endDate)} ·{' '}
              {inquiry.trip_json.timezone}
            </p>
            {segment.passengerCount && (
              <p>{segment.passengerCount} passengers</p>
            )}
            {segment.stops?.length ? (
              <p>Via {segment.stops.join(' · ')}</p>
            ) : null}
            {requirements(segment.vehicleRequirements)}
            {segment.notes && (
              <p className="mt-2 whitespace-pre-wrap">{segment.notes}</p>
            )}
          </li>
        ))}
      </ol>
      {plan.notes && (
        <p className="whitespace-pre-wrap text-sm">{plan.notes}</p>
      )}
      {plan.segments.length > 1 && (
        <p className="text-sm text-on-surface-variant">
          This request needs a quote covering each service segment. Review it in
          the operator quoting workflow; the quick single-trip quote action is
          unavailable.
        </p>
      )}
    </section>
  );
}
function InquiryNotifications({
  inquiry,
  onChange,
}: {
  inquiry: TripInquiry;
  onChange: () => void;
}) {
  const retry = useMutation(
    () => tripInquiryAPI.retryNotifications(inquiry.id),
    { onSuccess: onChange }
  );
  const messages = inquiry.notifications || [];
  return (
    <section className="border-t border-outline-variant pt-5 space-y-3">
      <h3 className="font-semibold">Customer and operator email</h3>
      <p className="text-xs text-on-surface-variant">
        Provider acceptance is separate from delivery. The saved inquiry remains
        available regardless of dispatch state.
      </p>
      {messages.map((message) => (
        <div key={message.id} className="flex justify-between gap-3 text-sm">
          <span>{inquiryLabel(message.kind)}</span>
          <span>
            {inquiryLabel(message.status)} · {message.attempts} attempts
            {message.providerStatus && ` · Provider: ${message.providerStatus}`}
            {message.errorCode && ` · ${message.errorCode}`}
          </span>
        </div>
      ))}
      {messages.some((message) =>
        ['failed', 'dead_letter', 'disabled'].includes(message.status)
      ) && (
        <button
          className="btn-secondary"
          disabled={retry.isLoading}
          onClick={() => retry.mutate()}
        >
          Retry eligible notifications
        </button>
      )}
      {!!retry.error && (
        <p role="alert" className="text-error">
          {inquiryError(retry.error)}
        </p>
      )}
    </section>
  );
}
function OpportunityDetail({ id, close }: { id: string; close: () => void }) {
  const cache = useQueryClient();
  const navigate = useNavigate();
  const [stage, setStage] = useState('');
  const [note, setNote] = useState('');
  const detail = useQuery(['trip-inquiry', id], () =>
    tripInquiryAPI.detail(id).then((response) => response.data)
  );
  const refresh = () => {
    cache.invalidateQueries(['trip-inquiry', id]);
    cache.invalidateQueries('trip-inquiries');
  };
  const update = useMutation(
    () =>
      tripInquiryAPI.stage(id, stage, detail.data!.version, note || undefined),
    { onSuccess: refresh }
  );
  const quote = useMutation(
    () => tripInquiryAPI.quote(id, detail.data!.version),
    {
      onSuccess: (response) => {
        refresh();
        navigate(
          `/admin/quotes?quote=${encodeURIComponent(response.data.quote.id)}`
        );
      },
    }
  );
  const approve = useMutation(
    () =>
      tripSourcingAPI.approve(id, {
        quoteId: detail.data!.active_quote_id!,
        expectedVersion: detail.data!.version,
        fulfillment: 'own',
      }),
    { onSuccess: refresh }
  );
  const inquiry = detail.data;
  const contact = inquiry?.contact_json;
  const trip = inquiry?.trip_json;
  return (
    <EntityDrawer
      open
      onClose={close}
      title={inquiry?.reference || 'Opportunity'}
      subtitle="Trip inquiry · Reservation opportunity"
      widthClassName="w-full max-w-4xl"
    >
      {detail.isLoading && <p role="status">Loading opportunity…</p>}
      {!!detail.error && <p role="alert">{inquiryError(detail.error)}</p>}
      {inquiry && contact && trip && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <span className="rounded-pill px-3 py-1 bg-primary-container text-on-surface text-sm">
              {inquiryLabel(inquiry.stage)}
            </span>
            <span className="text-xs text-on-surface-variant">
              Version {inquiry.version}
            </span>
          </div>
          <section className="admin-subpanel p-5 rounded-card">
            <h3 className="font-semibold">Requester</h3>
            <p className="mt-2">
              {contact.firstName} {contact.lastName}
            </p>
            <p className="text-sm break-all">
              {contact.email}
              {contact.phone && ` · ${contact.phone}`}
            </p>
            {contact.companyName && (
              <p className="text-sm">{contact.companyName}</p>
            )}
            <p className="text-xs text-on-surface-variant mt-3">
              Submitted contact details are unverified. They do not authorize
              access to existing customer records.
            </p>
          </section>
          <section className="admin-subpanel p-5 rounded-card">
            <h3 className="font-semibold">Trip requirements</h3>
            <dl className="mt-3 grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-on-surface-variant">Route</dt>
                <dd>
                  {trip.pickupLocation} → {trip.dropoffLocation}
                  {trip.stops?.length ? (
                    <p>Via {trip.stops.join(' · ')}</p>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt className="text-on-surface-variant">Service</dt>
                <dd>
                  {inquiryLabel(trip.serviceType || 'To be discussed')} ·{' '}
                  {trip.passengerCount || 'Unspecified'} passengers
                </dd>
              </div>
              <div>
                <dt className="text-on-surface-variant">Departure</dt>
                <dd>
                  {trip.startDate
                    ? new Date(trip.startDate).toLocaleString(undefined, {
                        timeZone: trip.timezone,
                      })
                    : 'To be discussed'}
                  <p>{trip.timezone}</p>
                </dd>
              </div>
              <div>
                <dt className="text-on-surface-variant">
                  Return / service end
                </dt>
                <dd>
                  {trip.endDate
                    ? new Date(trip.endDate).toLocaleString(undefined, {
                        timeZone: trip.timezone,
                      })
                    : 'To be discussed'}
                </dd>
              </div>
            </dl>
            {trip.serviceNotes && (
              <p className="whitespace-pre-wrap mt-4 text-sm">
                {trip.serviceNotes}
              </p>
            )}
            <p className="mt-4 text-sm">
              {inquiry.estimate_json?.pricing
                ? `Indicative estimate: ${inquiryMoney(
                    Number(inquiry.estimate_json.pricing.total)
                  )}`
                : 'Awaiting quote · No price committed'}
            </p>
          </section>
          <BookingPlanReview inquiry={inquiry} />
          {inquiry.converted_reservation_id ? (
            <Link
              className="btn-primary inline-flex"
              to={`/admin/reservations?reservation=${inquiry.converted_reservation_id}`}
            >
              Open reservation
            </Link>
          ) : (
            <section className="space-y-4">
              <h3 className="font-semibold">Move the opportunity forward</h3>
              <div className="flex gap-3 flex-wrap">
                {inquiry.active_quote_id ? (
                  <>
                    <Link
                      className="btn-primary"
                      to={`/admin/quotes?quote=${inquiry.active_quote_id}`}
                    >
                      Open linked quote
                    </Link>
                    <button
                      className="btn-secondary"
                      disabled={approve.isLoading}
                      onClick={() => approve.mutate()}
                    >
                      Approve quote with own inventory
                    </button>
                  </>
                ) : (
                  <button
                    className="btn-primary"
                    disabled={
                      quote.isLoading ||
                      inquiry.stage === 'lost' ||
                      !!trip.bookingPlan
                    }
                    onClick={() => quote.mutate()}
                  >
                    {trip.bookingPlan
                      ? 'Booking plan requires operator quote review'
                      : 'Create linked quote'}
                  </button>
                )}
              </div>
              {trip.bookingPlan && !inquiry.active_quote_id && (
                <Link className="btn-secondary inline-flex" to="/admin/quotes">
                  Open operator quoting workspace
                </Link>
              )}
              <p className="text-xs text-on-surface-variant">
                Creating a draft does not send a quote or confirm a reservation.
                Approval validates current inventory; the customer still reviews
                terms and payment requirements.
              </p>
              {!!(quote.error || approve.error) && (
                <p role="alert" className="text-error">
                  {inquiryError(quote.error || approve.error)}
                </p>
              )}
              <form
                className="space-y-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  update.mutate();
                }}
              >
                <label className="text-sm block">
                  Opportunity stage
                  <select
                    className="input-field w-full mt-2"
                    required
                    value={stage}
                    onChange={(event) => setStage(event.target.value)}
                  >
                    <option value="">Choose a stage</option>
                    {stages.map((value) => (
                      <option key={value} value={value}>
                        {inquiryLabel(value)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm block">
                  Operator note{' '}
                  <span className="text-on-surface-variant">(optional)</span>
                  <textarea
                    className="input-field w-full mt-2"
                    rows={2}
                    maxLength={2000}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                  />
                </label>
                {!!update.error && (
                  <p role="alert" className="text-error">
                    {inquiryError(update.error)}
                  </p>
                )}
                <button className="btn-secondary" disabled={update.isLoading}>
                  Update stage
                </button>
              </form>
            </section>
          )}
          {!inquiry.converted_reservation_id && (
            <ItineraryEditor
              key={`${inquiry.id}:${inquiry.version}`}
              inquiry={inquiry}
              onChange={refresh}
            />
          )}
          <OpportunitySourcing inquiry={inquiry} onChange={refresh} />
          <InquiryNotifications inquiry={inquiry} onChange={refresh} />
        </div>
      )}
    </EntityDrawer>
  );
}
export default function AdminTripOpportunities() {
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('inquiry')
  );
  const [showSettings, setShowSettings] = useState(false);
  const list = useQuery(
    ['trip-inquiries', search, stage, page],
    () =>
      tripInquiryAPI
        .list({
          search: search || undefined,
          stage: stage || undefined,
          page,
          limit: 20,
        })
        .then((response) => response.data),
    { keepPreviousData: true }
  );
  const settings = useQuery('trip-inquiry-settings', () =>
    tripInquiryAPI.settings().then((response) => response.data)
  );
  const items = list.data?.items || [];
  return (
    <PageLayout className="space-y-6">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Reservation opportunities</h1>
          <p className="text-on-surface-variant mt-1">
            Review customer inquiries, prepare quotes, and arrange capacity
            before confirmation.
          </p>
        </div>
        <div className="flex gap-2 items-start">
          <a
            href="/reserve"
            target="_blank"
            rel="noreferrer"
            className="btn-secondary"
          >
            Open customer form
          </a>
          <button
            className="btn-primary"
            onClick={() => setShowSettings((current) => !current)}
          >
            Form settings
          </button>
        </div>
      </div>
      {!!settings.error && (
        <p role="alert" className="text-error">
          Form settings unavailable: {inquiryError(settings.error)}
        </p>
      )}
      {showSettings && settings.data && (
        <SettingsForm
          settings={settings.data}
          close={() => setShowSettings(false)}
        />
      )}
      {!showSettings && settings.data && (
        <div className="admin-subpanel rounded-card px-5 py-4 flex flex-wrap justify-between gap-3 text-sm">
          <span>
            Customer form:{' '}
            <strong>
              {settings.data.enabled ? 'Accepting inquiries' : 'Closed'}
            </strong>{' '}
            ·{' '}
            {settings.data.mode === 'estimate'
              ? 'Indicative estimates enabled'
              : 'Team quoting'}
          </span>
          <span>
            Share{' '}
            <a className="text-primary underline" href="/reserve">
              {window.location.origin}/reserve
            </a>
          </span>
        </div>
      )}
      <div className="flex flex-wrap gap-4">
        <label className="text-sm flex-1 min-w-[180px]">
          Search inquiries
          <input
            className="input-field block w-full mt-2"
            type="search"
            placeholder="Name, email, reference, or route"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </label>
        <label className="text-sm">
          Stage
          <select
            className="input-field block mt-2"
            value={stage}
            onChange={(event) => {
              setStage(event.target.value);
              setPage(1);
            }}
          >
            <option value="">All stages</option>
            {[
              ...stages,
              'quoted',
              'awaiting_confirmation',
              'booked',
              'cancelled',
            ].map((value) => (
              <option key={value} value={value}>
                {inquiryLabel(value)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {list.isLoading && <p role="status">Loading inquiries…</p>}
      {!!list.error && (
        <div role="alert" className="text-error">
          {inquiryError(list.error)}{' '}
          <button className="underline" onClick={() => list.refetch()}>
            Retry
          </button>
        </div>
      )}
      <div className="admin-subpanel rounded-card overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-surface-container-high">
            <tr>
              {['Inquiry / requester', 'Trip', 'Stage', 'Price', ''].map(
                (label, i) => (
                  <th key={i} className="p-4 font-medium">
                    {label}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((inquiry) => (
              <tr key={inquiry.id} className="border-t border-outline-variant">
                <td className="p-4">
                  <button
                    className="text-primary font-semibold"
                    onClick={() => setSelectedId(inquiry.id)}
                  >
                    {inquiry.reference}
                  </button>
                  <p>
                    {inquiry.contact_json.firstName}{' '}
                    {inquiry.contact_json.lastName}
                  </p>
                  <p className="text-on-surface-variant text-xs">
                    {inquiry.contact_json.email}
                  </p>
                </td>
                <td className="p-4">
                  <p>
                    {inquiry.trip_json.pickupLocation} →{' '}
                    {inquiry.trip_json.dropoffLocation}
                  </p>
                  <p className="text-xs text-on-surface-variant">
                    {inquiry.trip_json.startDate
                      ? new Date(inquiry.trip_json.startDate).toLocaleString(
                          undefined,
                          { timeZone: inquiry.trip_json.timezone }
                        )
                      : 'Timing to be discussed'}
                  </p>
                </td>
                <td className="p-4">{inquiryLabel(inquiry.stage)}</td>
                <td className="p-4">
                  {inquiry.estimate_json?.pricing
                    ? inquiryMoney(Number(inquiry.estimate_json.pricing.total))
                    : 'Awaiting quote'}
                </td>
                <td className="p-4">
                  <button
                    aria-label={`Review ${inquiry.reference}`}
                    onClick={() => setSelectedId(inquiry.id)}
                  >
                    <ArrowRightIcon className="h-5 w-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.isLoading && !list.error && !items.length && (
          <div className="p-10 text-center">
            <h2 className="font-semibold">No opportunities yet</h2>
            <p className="text-sm text-on-surface-variant mt-2">
              Share your customer form to collect trip requests here.
            </p>
          </div>
        )}
      </div>
      {list.data && (
        <div className="flex justify-between items-center text-sm">
          <span>{list.data.total} inquiries</span>
          <div className="flex gap-3">
            <button
              className="btn-secondary"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <button
              className="btn-secondary"
              disabled={page * 20 >= list.data.total}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
      {selectedId && (
        <OpportunityDetail id={selectedId} close={() => setSelectedId(null)} />
      )}
    </PageLayout>
  );
}
